'use client';

import type { FC } from 'react';
import { use, useEffect } from 'react';
import { TopErrorManagerContext } from '../TopErrorManagerContext';
import { RenderError } from '../RenderError';

/**
 * Reports suspends that hit the top-level Suspense boundary.
 *
 * _Note_: Known false positive: Next.js `notFound()` and `redirect()` throw errors that
 * can trigger this boundary's fallback on 404/redirect pages, so this reporter
 * may fire there even though it's expected framework behavior. See links below for tracking.
 *
 * @see https://github.com/vercel/next.js/discussions/70488
 * @see https://github.com/vercel/next.js/discussions/61486
 * @see https://github.com/vercel/next.js/discussions/71927
 * @see https://github.com/facebook/react/pull/32069
 * @see https://github.com/facebook/react/pull/32163
 * @see https://github.com/facebook/react/pull/32224
 */
export const TopLevelSuspenseReporter: FC = () => {
  const { addError } = use(TopErrorManagerContext);

  useEffect(
    function reportTopLevelSuspenseError() {
      addError(
        new RenderError({
          error: new Error(
            'A component suspended on the client to the top-level Suspense boundary. This is likely due to a missing Suspense boundary above the component, or not calling the update within a startTransition().'
          )
        })
      );
    },
    [addError]
  );

  return null;
};
